import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import Placeholder from "@/components/Placeholder";

const CATEGORIES = {
  service: {
    label: "Service",
    tint: "maroon",
    items: [
      { title: "Nature's Dividend", desc: "Investing in nature, creating lasting impact.", image: "/images/events/natures-dividend.jpg" },
      { title: "Summer Saheli", desc: "Care-driven initiative supporting communities.", image: "/images/events/summer-saheli.jpg" },
      { title: "Smile Safari", desc: "Bringing joy and smiles to children.", image: "/images/events/smile-safari.jpg" },
      { title: "Naari Care", desc: "Supporting and empowering women.", image: "/images/events/naari-care.jpg" },
      { title: "Little Veer", desc: "Encouraging young minds.", image: "/images/events/little-veer.jpg" },
      { title: "Langar", desc: "Serving meals with care.", image: "/images/events/langar.jpg" },
      { title: "With Lions", desc: "Collaborative initiatives with Lions Club.", image: "/images/events/with-lions.jpg" },
      { title: "Voice for the Voiceless", desc: "Advocating for animal welfare and care.", image: "/images/events/voice-for-the-voiceless.jpg" },
    ],
  },
  fellowship: {
    label: "Fellowship",
    tint: "ember",
    items: [
      { title: "Laser War", desc: "High-energy bonding experience.", image: "/images/events/laser-war.jpg" },
      { title: "Meet & Greet with Lions", desc: "Building meaningful connections.", image: "/images/events/meet-greet.jpg" },
      { title: "Carter Showdown", desc: "Friendly competition and team spirit.", image: "/images/events/carter-showdown.jpg" },
    ],
  },
  leadership: {
    label: "Leadership",
    tint: "gold",
    items: [
      { title: "Coding Session", desc: "Hands-on learning experience.", image: "/images/events/coding-session.jpg" },
      { title: "Marketing Session by Lion Navdeep Nigam", desc: "Real-world marketing insights.", image: "/images/events/marketing-session.jpg" },
      { title: "Lead Yourself by Lion Divya Bajaj", desc: "Self-growth and leadership development.", image: "/images/events/lead-yourself.jpg" },
      { title: "Mauka E Manch", desc: "Where talent meets opportunity, and confidence takes centre stage.", image: "/images/events/mauka-e-manch.jpg" },
    ],
  },
};

const TINT_MAP = ["maroon", "ember", "gold", "ink", "cream"];

export default function Events() {
  const location = useLocation();
  const tabsRef = useRef(null);

  // Determine initial tab from ?category=
  const initialTab = (() => {
    const params = new URLSearchParams(location.search);
    const c = params.get("category");
    return c && CATEGORIES[c] ? c : "service";
  })();
  const [active, setActive] = useState(initialTab);

  // On mount / query change, switch tab and scroll to the tabs section.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get("category");
    if (c && CATEGORIES[c]) {
      setActive(c);
      // Wait for layout, then scroll the tabs strip into view
      const t = setTimeout(() => {
        if (tabsRef.current) {
          tabsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 250);
      return () => clearTimeout(t);
    }
  }, [location.search]);
  const cat = CATEGORIES[active];

  return (
    <div className="pt-32 md:pt-40">
      {/* HEADER */}
      <section className="pb-16 md:pb-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-6">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> Events
            </div>
            <h1 className="font-playfair text-5xl md:text-7xl lg:text-[7.5rem] font-semibold text-[#2C2C2C] leading-[0.95] tracking-tight max-w-5xl">
              Moments that <em className="text-[#8B1E2D]">shape us</em>.
            </h1>
            <p className="mt-8 font-inter text-[#555555] text-lg md:text-xl leading-relaxed max-w-2xl">
              From meaningful service to shared experiences and learning, every event adds to who we are as a community.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TABS */}
      <section ref={tabsRef} className="bg-white scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 border-t border-black/10">
          <div className="flex flex-wrap items-center gap-2 md:gap-10 py-6" role="tablist" data-testid="events-tabs">
            {Object.entries(CATEGORIES).map(([key, c]) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                className={`relative font-inter text-sm md:text-base tracking-wide py-2 pr-3 transition-colors ${
                  active === key ? "text-[#8B1E2D]" : "text-[#555555] hover:text-[#2C2C2C]"
                }`}
                data-testid={`events-tab-${key}`}
              >
                <span className="font-playfair text-xl md:text-2xl mr-3">0{Object.keys(CATEGORIES).indexOf(key) + 1}</span>
                {c.label}
                {active === key && (
                  <motion.span
                    layoutId="tab-underline"
                    className="absolute left-0 right-3 -bottom-[1px] h-[2px] bg-[#8B1E2D]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 md:py-24 bg-[#F7F3EE]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid={`events-grid-${active}`}
            >
              {cat.items.map((ev, i) => (
                <motion.article
                  key={ev.title}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="group bg-white border border-black/5 flex flex-col transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(139,30,45,0.25)]"
                  data-testid={`event-card-${ev.title.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
                >
                  <div className="overflow-hidden">
                    {ev.image ? (
                      <div className="aspect-[4/3] w-full overflow-hidden">
                        <img
                          src={ev.image}
                          alt={ev.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform ease-out group-hover:scale-105"
                          style={{ transitionDuration: "400ms" }}
                        />
                      </div>
                    ) : (
                      <div className="transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105">
                        <Placeholder
                          tint={TINT_MAP[i % TINT_MAP.length]}
                          ratio="aspect-[4/3]"
                          solid
                          label={ev.title}
                          index={`${cat.label} · 0${i + 1}`}
                        />
                      </div>
                    )}
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="font-inter text-[10px] tracking-[0.3em] uppercase text-[#E76F2E] mb-3">
                      {cat.label}
                    </div>
                    <h3 className="font-playfair text-xl md:text-2xl font-semibold text-[#2C2C2C] leading-snug">
                      {ev.title}
                    </h3>
                    <p className="mt-3 font-inter text-[#555555] text-sm leading-relaxed">
                      {ev.desc}
                    </p>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
